import { useField } from "formik";
import { useCallback, useMemo } from "react";
import { CustomSelect, CustomSelectProps, isItem, Item } from "./CustomSelect";

export type FormikCustomSelectProps<T extends Item> = {
  name: string;
  onChange?: CustomSelectProps<T>["onChange"];
} & Omit<CustomSelectProps<T>, "selectedItem" | "onChange">;

export function FormikCustomSelect<T extends Item>({
  name,
  onChange,
  ...props
}: FormikCustomSelectProps<T>) {
  const { items } = props;

  const [field, meta, helper] = useField(name);
  const selectedItem = useMemo(() => {
    return (
      items.filter(isItem).find((item) => item.key === field.value) ?? null
    );
  }, [field.value, items]);

  const handleChange = useCallback(
    async (item: T | null) => {
      await helper.setValue(item?.key ?? null);
      await helper.setTouched(true);
      onChange?.(item);
    },
    [helper, onChange]
  );

  const onBlur = useCallback(async () => {
    await helper.setTouched(true);
  }, [helper]);

  return (
    <CustomSelect<T>
      {...props}
      error={meta.touched ? meta.error : undefined}
      onChange={handleChange}
      selectedItem={selectedItem}
      onBlur={onBlur}
    />
  );
}
